import type {
  SlideTransition,
  TransitionDirection,
  TransitionType,
} from "@/types/document";

// Slide-transition model + the CSS it drives (TODO §10). The keyframes live in styles.css
// (`slide-enter`/`slide-leave`); this module owns the *values* — the picker's option lists,
// the easing name→CSS map (§10.1.3), the duration clamp (§10.1.2), and `motionStyle`, which
// turns a transition + nav direction into the inline CSS custom properties those two
// keyframes read. One keyframe pair serves every type/duration/easing/direction (§10.2.1).

export const TRANSITION_TYPES: { value: TransitionType; label: string }[] = [
  { value: "none", label: "None" },
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide (cover)" },
  { value: "push", label: "Push" },
  { value: "zoom", label: "Zoom" },
  { value: "flip", label: "Flip" },
];

// §10.1.3 Friendly easing names → valid CSS timing functions. Stored as the KEY; resolved
// here so the emitted CSS is always valid and the picker stays a fixed list.
export const EASINGS: { value: string; label: string; css: string }[] = [
  { value: "ease-in-out", label: "Ease in-out", css: "ease-in-out" },
  { value: "ease", label: "Ease", css: "ease" },
  { value: "ease-in", label: "Ease in", css: "ease-in" },
  { value: "ease-out", label: "Ease out", css: "ease-out" },
  { value: "smooth", label: "Smooth", css: "cubic-bezier(0.4, 0, 0.2, 1)" },
  { value: "snappy", label: "Snappy", css: "cubic-bezier(0.65, 0, 0.35, 1)" },
];

export function resolveEasing(key: string): string {
  return EASINGS.find((e) => e.value === key)?.css ?? "ease-in-out";
}

export const DIRECTIONS: { value: TransitionDirection; label: string }[] = [
  { value: "left", label: "Left" },
  { value: "right", label: "Right" },
  { value: "up", label: "Up" },
  { value: "down", label: "Down" },
];

export const DURATION_MIN = 150;
export const DURATION_MAX = 1000;

export const DEFAULT_TRANSITION: SlideTransition = {
  type: "fade",
  durationMs: 400,
  easing: "ease-in-out",
  direction: "left",
};

export function clampDuration(ms: number): number {
  if (!Number.isFinite(ms)) return DEFAULT_TRANSITION.durationMs;
  return Math.min(DURATION_MAX, Math.max(DURATION_MIN, Math.round(ms)));
}

// Only slide/push are directional; the picker hides the direction toggle otherwise (§10.3.2).
export function usesDirection(type: TransitionType): boolean {
  return type === "slide" || type === "push";
}

// Whether the type animates at all — `none` is an instant cut with no second slide mounted.
export function isAnimated(type: TransitionType): boolean {
  return type !== "none";
}

// The unit "advance" vector for a direction: the way outgoing content travels on a FORWARD
// step. Going back flips it (× navDir), so back reverses the motion (§10.2.3).
const DIR_VEC: Record<TransitionDirection, [number, number]> = {
  left: [-1, 0],
  right: [1, 0],
  up: [0, -1],
  down: [0, 1],
};

type CssVars = Record<string, string | number>;

// §10.2.1/§10.2.2/§10.2.3 Inline CSS custom properties for one participant in a transition.
// `role` picks the entering (incoming) or leaving (outgoing) slide; `navDir` is +1 forward /
// -1 back. The `slide-enter`/`slide-leave` keyframes read these vars (with identity
// fallbacks), so a single keyframe pair expresses fade/slide/push/zoom/flip in every
// direction. Uses only opacity + transform (translate/scale/rotateY) — never layout props.
export function motionStyle(
  t: SlideTransition,
  navDir: 1 | -1,
  role: "enter" | "leave",
): React.CSSProperties {
  const vars: CssVars = {
    "--slide-dur": `${clampDuration(t.durationMs)}ms`,
    "--slide-ease": resolveEasing(t.easing),
  };

  const [bx, by] = DIR_VEC[t.direction ?? "left"];
  // Outgoing travels toward `adv`; incoming arrives from `-adv`.
  const advX = bx * navDir;
  const advY = by * navDir;

  if (role === "enter") {
    switch (t.type) {
      case "fade":
        vars["--enter-opacity"] = 0;
        break;
      case "slide": // incoming covers the (static) outgoing slide
      case "push": // incoming and outgoing move together
        vars["--enter-x"] = `${-advX * 100}%`;
        vars["--enter-y"] = `${-advY * 100}%`;
        break;
      case "zoom":
        vars["--enter-scale"] = 0.85;
        vars["--enter-opacity"] = 0;
        break;
      case "flip":
        vars["--enter-rotate"] = `${navDir > 0 ? 90 : -90}deg`;
        vars["--enter-opacity"] = 0;
        break;
    }
  } else {
    switch (t.type) {
      case "fade":
        vars["--leave-opacity"] = 0;
        break;
      case "slide":
        // Cover: the outgoing slide holds still beneath the incoming one, then unmounts.
        break;
      case "push":
        vars["--leave-x"] = `${advX * 100}%`;
        vars["--leave-y"] = `${advY * 100}%`;
        break;
      case "zoom":
        vars["--leave-scale"] = 1.1;
        vars["--leave-opacity"] = 0;
        break;
      case "flip":
        vars["--leave-rotate"] = `${navDir > 0 ? -90 : 90}deg`;
        vars["--leave-opacity"] = 0;
        break;
    }
  }

  return vars as React.CSSProperties;
}
